// Deal prices on the page follow the basket (Michael 2026-10-06: "$7.50"
// becomes "$7.50 crossed out, $5" once there's a tea in the basket). Liquid
// paints the right state first (snippets/deals-rules.liquid, generated from the
// deals program's rules); this keeps it right as the basket changes, with the
// deals program's own pricing - the very function its server charges with.
//
//   [data-deal-offer data-product data-type data-cents]  a product on a deal; inside it:
//     [data-deal-when="on"|"off"]           shown when the deal is on / off
//     [data-product-price][data-deal-unit][data-base-unit]
//                                           PDP button price (x quantity, product-price.js)
//     [data-deal-amount data-on data-off]   a card's price
//
// Cart lines and totals need none of this: cardo re-renders them from Liquid.

import { priceBasket, SMALL_TINS } from '@deals/core/price'
import { fromAjaxCart } from '@deals/core/basket'
import { formatMoney } from './product-price.js'
import { dealContext } from './deal-unlock.js'
import { paint as paintTinNote } from './tin-deal-note.js'

let cart = null
// Off unless the theme's Deals API setting is set (layout/theme.liquid).
const live = Boolean(document.documentElement.dataset.dealsApi)

/** What one more of this product would cost with the basket as it is. */
function unitWith(product, type, cents) {
  const basket = fromAjaxCart(cart)
  basket.lines.push({ variantId: 'probe', quantity: 1, properties: {}, productId: product, productType: type, unitPrice: cents })
  return priceBasket(basket, undefined, dealContext()).lines.at(-1).now
}

/** "with tea" when a tea is in the basket, else "with your china" (Teaware). */
function byWhat(triggers) {
  const types = triggers.split('|')
  const tea = cart.items.some(
    (i) => i.quantity > 0 && i.product_type !== 'Teaware' && types.includes(i.product_type)
  )
  return tea ? 'with tea' : 'with your china'
}

/** The note's "$2.50 off with tea": saving x the buy box quantity, and why. */
function note(el) {
  const qty = Number(el.querySelector('input[name="quantity"]')?.value) || 1
  el.querySelectorAll('[data-deal-saving]').forEach((x) => {
    x.textContent = formatMoney((Number(x.dataset.each) * qty) / 100)
  })
  el.querySelectorAll('[data-deal-by]').forEach((x) => {
    x.textContent = byWhat(x.dataset.triggers || '')
  })
}

function apply(el) {
  const cents = Number(el.dataset.cents)
  const unit = unitWith(el.dataset.product, el.dataset.type, cents)
  const on = unit < cents
  note(el)
  if (el.hasAttribute('data-deal-on') === on) return
  el.toggleAttribute('data-deal-on', on)
  el.querySelectorAll('[data-deal-when]').forEach((x) => {
    x.hidden = (x.dataset.dealWhen === 'on') !== on
  })
  el.querySelectorAll('[data-deal-amount]').forEach((x) => {
    x.textContent = formatMoney(Number(on ? x.dataset.on : x.dataset.off) / 100)
  })
  el.querySelectorAll('[data-product-price][data-deal-unit]').forEach((x) => {
    x.dataset.unitPrice = on ? x.dataset.dealUnit : x.dataset.baseUnit
    x.dispatchEvent(new CustomEvent('deal:price'))
  })
}

/**
 * What adding `qty` of a product changes the basket's deal total by, per unit:
 * its own price, less any saving it switches on for what's already there (a
 * tea makes the infusers in the basket $5). Michael 2026-10-06: the "$X away
 * from free shipping" on a tea page was out by the infusers' saving. With
 * the variant, a small tin counts in the ad's tin deal ($15 after the 3rd).
 */
function addEach(product, type, cents, qty, variant = 'probe') {
  const basket = fromAjaxCart(cart)
  const ctx = dealContext()
  const before = priceBasket(basket, undefined, ctx).now
  basket.lines.push({ variantId: variant, quantity: qty, properties: {}, productId: product, productType: type, unitPrice: cents })
  return (priceBasket(basket, undefined, ctx).now - before) / qty
}

/**
 * The free-shipping goals count what the buy box would add (cardo's goal adds
 * data-cardo-goal-add x quantity to the basket's total, which is already the
 * deal total - see the cardo:cart listener). Set it per unit, so cardo's own
 * arithmetic lands on the exact deal total.
 */
function goals(form) {
  const qty = Number(form.querySelector('input[name="quantity"]')?.value) || 1
  const each = String(
    addEach(form.dataset.goalProduct, form.dataset.goalType || '', Number(form.dataset.goalCents), qty, form.dataset.goalVariant)
  )
  form.querySelectorAll('[data-cardo-goal-add]').forEach((x) => x.setAttribute('data-cardo-goal-add', each))
  if (form.id) {
    document
      .querySelectorAll(`[data-cardo-goal-form="${form.id}"]`)
      .forEach((x) => x.setAttribute('data-cardo-goal-add', each))
  }
}

/**
 * What the basket's small tins cost, in cents, with `qty` more of this one.
 * Tins only: an infuser the tin would make $5 isn't the tin's price
 * (2026-10-11 review: the tin showed $22.50 with an infuser in the basket).
 */
function tinsTotal(el, qty) {
  const basket = fromAjaxCart(cart)
  if (qty > 0) {
    const { variant, product, type, cents } = el.dataset
    basket.lines.push({ variantId: variant, quantity: qty, properties: {}, productId: product, productType: type || '', unitPrice: Number(cents) })
  }
  return priceBasket(basket, undefined, dealContext())
    .lines.filter((p) => SMALL_TINS.includes(basket.lines[p.line].variantId))
    .reduce((n, p) => n + p.was * basket.lines[p.line].quantity - p.saving, 0)
}

/** The buy box quantity when the box is buying this tin (a size card sits outside the form), else `other`. */
function tinQty(el, other = 1) {
  const form = el.closest('form') || document.getElementById(`product-form-${el.dataset.product}`)
  if (form?.querySelector('input[name="id"]')?.value !== el.dataset.variant) return other
  return Number(form.querySelector('input[name="quantity"]')?.value) || 1
}

/**
 * A small tin in the ad's tin deal (Michael 2026-10-11): its button and size
 * card say what one more really costs - "$25" crossed out, "$10" for the 3rd,
 * "$15" after. [data-tin-next data-product data-type data-cents data-variant],
 * with the same [data-deal-when] / [data-product-price] / [data-deal-amount]
 * inside as a deal offer. The button's price is for the whole quantity; the
 * size card's is the next tin after the basket and the box (Michael
 * 2026-10-11: "When you've got two tins in the cart, it needs to say $10
 * because that's your next tin"), as the line under the button counts them.
 */
function tin(el) {
  const cents = Number(el.dataset.cents)
  const form = el.tagName === 'FORM'
  const qty = tinQty(el, form ? 1 : 0)
  const total = tinsTotal(el, form ? qty : qty + 1)
  const each = Math.min(cents, form ? (total - tinsTotal(el, 0)) / qty : total - tinsTotal(el, qty))
  // its own parts only: a size card's price can sit inside the button's form
  const own = (sel) => [...el.querySelectorAll(sel)].filter((x) => x.closest('[data-tin-next]') === el)
  own('[data-deal-when]').forEach((x) => {
    x.hidden = (x.dataset.dealWhen === 'on') !== each < cents
  })
  own('[data-deal-amount]').forEach((x) => (x.textContent = formatMoney(each / 100)))
  own('[data-product-price]').forEach((x) => {
    x.dataset.unitPrice = String(each / 100)
    x.dispatchEvent(new CustomEvent('deal:price'))
  })
}

const applyAll = (root = document) => {
  if (!cart) return
  root.querySelectorAll('[data-deal-offer]').forEach(apply)
  root.querySelectorAll('[data-tin-next]').forEach(tin)
  root.querySelectorAll('form[data-goal-cents]').forEach(goals)
}

// Capture phase, so this runs before cardo's goal module reads the total: the
// free-shipping bar and "$X more" lines count what the shopper will actually
// pay - the deal total, which is also what checkout's free-shipping rate sees.
if (live) {
  document.addEventListener(
    'cardo:cart',
    (e) => {
      cart = e.detail.cart
      const saving = priceBasket(fromAjaxCart(cart), undefined, dealContext()).saving
      if (saving > 0 && !cart.deals_saving) {
        cart.deals_saving = saving
        cart.total_price -= saving
      }
      applyAll()
    },
    true
  )
  // A swapped section (another size): reprice it, then let cardo's goals redraw.
  document.addEventListener('ui:hydrate', (e) => {
    const root = e.detail?.target || document
    applyAll(root)
    // cardo's goals redraw on a quantity input (no cardo:render here: that
    // would hydrate again, round and round).
    if (cart) {
      root
        .querySelectorAll?.('form[data-goal-cents] input[name="quantity"]')
        .forEach((q) => q.dispatchEvent(new Event('input', { bubbles: true })))
    }
  })
  // The pass going live or ending changes a small tin's price.
  document.addEventListener('deal:unlock', () => applyAll())
  // The buy box quantity changes the saving in the note ("$5 off with tea" for
  // 2), a small tin's prices and tin deal line, and what the goals count.
  // Capture phase: before cardo's goal redraws. qty:change comes from the +/-
  // box's wrapper, not the input.
  const onQty = (e) => {
    const q = e.target.name === 'quantity' ? e.target : e.target.querySelector?.('input[name="quantity"]')
    if (!q || !cart) return
    const offer = q.closest('[data-deal-offer]')
    if (offer) note(offer)
    // the button and the size cards (outside the form) of this product
    const id = q.closest('form')?.dataset.productId
    if (id) document.querySelectorAll(`[data-tin-next][data-product="${id}"]`).forEach(tin)
    if (q.form?.querySelector('[data-tin-deal-note]')) paintTinNote(q.form)
    const form = q.closest('form[data-goal-cents]')
    if (form) goals(form)
  }
  document.addEventListener('input', onQty, true)
  document.addEventListener('change', onQty, true)
  document.addEventListener('qty:change', onQty, true)
}
